import { grpc } from '@improbable-eng/grpc-web';
import { CHECKOUT_COPY } from 'lib/billing';
import type { OverdueInvoice } from './types';

// The backend's answer for an overdue invoice that was cancelled, settled or voided.
export const isOverdueUnpayable = (e: any) => e?.code === grpc.Code.FailedPrecondition;

// What crypto must add for the balance to settle an overdue invoice in full; a debt on the balance is
// added too, or the settle falls short.
export const overdueCryptoTopUpCents = (dueCents: number, creditCents: number) => Math.max(0, dueCents - creditCents);

export const OVERDUE_RECHECK = {
  payable: 'payable',
  unpayable: 'unpayable',
  changed: 'changed'
} as const;

export type OverdueRecheck = {
  result: (typeof OVERDUE_RECHECK)[keyof typeof OVERDUE_RECHECK];
  fresh?: OverdueInvoice;
};

// Resolve an overdue subscription to its payable invoice (id + amount) via the
// billing backend. Shared by every "pay overdue" entry point (banner switch,
// renew button, main-page pay-now).
export async function fetchOverdueInvoice(
  getBillingClient: () => Promise<any>,
  userId: string,
  subscriptionId: string
): Promise<OverdueInvoice> {
  const client = await getBillingClient();
  let res: any;
  try {
    res = await client.getOverDueInfo({ userId, subscriptionId });
  } catch (e: any) {
    // Cancelled, settled or voided: every entry point shows the same message; code kept for callers.
    if (isOverdueUnpayable(e)) throw Object.assign(new Error(CHECKOUT_COPY.payment.overdue.noLongerPayable), { code: e.code });
    throw e;
  }
  return {
    invoiceId: res?.invoiceId || '',
    subscriptionId,
    amountDueCents: Number(res?.amountDue || 0),
    currency: res?.currency || undefined
  };
}

// Re-reads the invoice a drawer opened with, right before paying it: it may have
// died (sub cancelled, invoice settled or voided) or its amount may have changed.
export async function recheckOverdueInvoice(
  getBillingClient: () => Promise<any>,
  userId: string,
  current: OverdueInvoice
): Promise<OverdueRecheck> {
  let fresh: OverdueInvoice;
  try {
    fresh = await fetchOverdueInvoice(getBillingClient, userId, current.subscriptionId);
  } catch (e: any) {
    if (isOverdueUnpayable(e)) return { result: OVERDUE_RECHECK.unpayable };
    throw e;
  }
  if (!fresh.invoiceId) return { result: OVERDUE_RECHECK.unpayable };
  if (fresh.invoiceId === current.invoiceId && fresh.amountDueCents === current.amountDueCents) {
    return { result: OVERDUE_RECHECK.payable };
  }
  return { result: OVERDUE_RECHECK.changed, fresh };
}
